import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import InvoiceList from "./InvoiceList";
import getSelectClassNames from "components/SelectFieldClasses/SelectFieldClasses";
import Select from "react-tailwindcss-select";
import userResources from "api/userResources";
import PhoneInputWithCountrySelect from "react-phone-number-input";

export default function SearchInvoice() {
  const location = useLocation();
  const [supplierData, setSupplierData] = useState([]);
  const [customerData, setCustomerData] = useState([]);
  const [customerSearch, setCustomerSearch] = useState();
  const [searchParams, setSearchParams] = useState(() => {
    const savedParams = sessionStorage.getItem("searchInvoiceParams");
    return savedParams ? JSON.parse(savedParams) : null;
  });
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useForm({
    defaultValues: searchParams || {},
  });
  const getSupplier = async () => {
    try {
      // Assuming userResources.supplier returns a promise that resolves to your API response
      const response = await userResources.supplier({ page: 0, size: 99999 });
      console.log("Supplier response:", response);

      // Safely mapping over the nested data. You might want to confirm that the API always returns the nested structure.
      const supplierList =
        response?.data?.data?.map((supplier) => ({
          value: supplier.id,
          label: supplier.name,
        })) || [];

      setSupplierData(supplierList);
    } catch (error) {
      console.error("Error fetching supplier data:", error);
    }
  };

  const getCustomers = async (searchValue) => {
    // If searchValue is undefined or empty, clear customerData and exit early.
    if (!searchValue) {
      setCustomerData([]);
      return;
    }
    const params = { email: searchValue };
    try {
      const response = await userResources.customerList(params);
      const data = response.data.data.map((item) => ({
        value: item.id,
        label: item.email,
      }));
      setCustomerData(data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    // Set a debounce timer to delay the API call by 500ms.
    const timer = setTimeout(() => {
      if (customerSearch) {
        getCustomers(customerSearch);
      } else {
        setCustomerData([]);
      }
    }, 500);

    // Clear the timer if customerSearch changes before the timeout is reached.
    return () => clearTimeout(timer);
  }, [customerSearch]);

  useEffect(() => {
    if (searchParams) {
      sessionStorage.setItem(
        "searchInvoiceParams",
        JSON.stringify(searchParams)
      );
    }
  }, [searchParams]);

  // Watch all fields for changes
  const watchedFields = watch();

  useEffect(() => {
    if (errors.form) {
      clearErrors("form");
    }
  }, [watchedFields, errors.form, clearErrors]);

  const onSubmit = (formData) => {
    const hasValue = Object.values(formData).some((val) => val);
    if (!hasValue) {
      setError("form", {
        type: "manual",
        message: "At least one field is required.",
      });
      toast.warning("Please fill at least one field.");
      return;
    }

    setSearchParams(formData); // ✅ Save input correctly
    sessionStorage.setItem("searchInvoiceParams", JSON.stringify(formData));
    clearErrors("form");
  };

  useEffect(() => {
    if (location?.state) {
      const newParams = { phone_number: location.state }; // Only store phone_number
      setSearchParams(newParams);
      sessionStorage.setItem("searchInvoiceParams", JSON.stringify(newParams));

      // Update form field
      reset(newParams);
    }
  }, [location?.state, reset]);

  useEffect(() => {
    getSupplier();
  }, []);

  return (
    <div className="mt-4 bg-gray-900 py-6 text-white sm:mx-4">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="grid gap-6 pb-4 sm:pb-0"
      >
        {/* Input Fields */}
        <div className="mx-2 grid grid-cols-2 items-end gap-4 sm:grid-cols-4 md:grid-cols-4">
          <div>
            <label
              htmlFor="invoice_number"
              className="mb-2 block text-sm font-medium text-white"
            >
              Invoice Number
            </label>
            <input
              type="text"
              id="invoice_number"
              placeholder="Enter invoice number"
              className="w-full rounded border border-gray-300 bg-white p-2 text-gray-800"
              {...register("invoice_number")}
            />
          </div>
          <div>
            <label
              htmlFor="ticket_number"
              className="mb-2 block text-sm font-medium text-white"
            >
              Ticket Number
            </label>
            <input
              type="text"
              id="ticket_number"
              placeholder="Enter Ticket number"
              className="w-full rounded border border-gray-300 bg-white p-2 text-gray-800"
              {...register("ticket_number")}
            />
          </div>
          <div>
            <label
              htmlFor="email"
              className="mb-2 block text-sm font-medium text-white"
            >
              Customer Email
            </label>
            <Select
              {...register(`customer_id`)}
              onSearchInputChange={(e) => {
                setCustomerSearch(e.target.value);
              }}
              value={customerData.find(
                (option) => option.value === watch("customer_id") // Use watch to get the current value from React Hook Form
              )} // Controlled value
              onChange={(selectedOption) => {
                setValue("customer_id", selectedOption?.value || ""); // Update the value using setValue from React Hook Form
                clearErrors("customer_id");
              }}
              options={customerData} // Format options
              isDisabled={false}
              isSearchable={true} // Enable search functionality
              placeholder="Search Customer"
              classNames={getSelectClassNames({
                isDisabled: false,
                selectProps: {},
                isSelected: false,
                value: watch("customer_id"), // Use watch to get the current value
              })} // Pass parameters here
              formatOptionLabel={(data) => (
                <li
                  className={`mb-1 block cursor-pointer select-none truncate rounded px-2 py-2 transition duration-200 ${
                    !data.isSelected
                      ? `bg-gray-200 text-gray-900`
                      : `bg-blue-800 text-gray-200`
                  }`}
                >
                  {data.label}
                </li>
              )}
            />
          </div>
          {/* <div>
            <label
              htmlFor="phone_number"
              className="mb-2 block text-sm font-medium text-white"
            >
              Phone Number
            </label>
            <PhoneInputWithCountrySelect
              defaultCountry="CA"
              value={watch("phone_number")}
              onChange={(value) => {
                setValue("phone_number", value); // Update the useForm state
              }}
              placeholder="Enter Phone Number"
              className="w-full rounded-md border p-2 text-lg focus:outline-none focus:ring-2 dark:!border-white/10"
            />
          </div> */}
          <div>
            <label
              htmlFor="booking_date"
              className="mb-2 block text-sm font-medium text-white"
            >
              Booking Date
            </label>
            <input
              type="date"
              id="booking_date"
              placeholder="Enter booking Date"
              className="w-full rounded border border-gray-300 bg-white p-2 text-gray-800"
              {...register("booking_date")}
            />
          </div>

          <div>
            <label
              htmlFor="departure_date"
              className="mb-2 block text-sm font-medium text-white"
            >
              Departure Date
            </label>
            <input
              type="date"
              id="departure_date"
              placeholder="Enter departure Date"
              className="w-full rounded border border-gray-300 bg-white p-2 text-gray-800"
              {...register("departure_date")}
            />
          </div>
          <div>
            <label className="my-2 block text-sm font-medium">Supplier</label>
            <Select
              {...register(`supplier_id`)}
              value={supplierData.find(
                (option) => option.value === watch("supplier_id") // Use watch to get the current value from React Hook Form
              )} // Controlled value
              onChange={(selectedOption) => {
                setValue("supplier_id", selectedOption?.value || ""); // Update the value using setValue from React Hook Form
                clearErrors("supplier_id");
              }}
              options={supplierData} // Format options
              isDisabled={false}
              isSearchable={true} // Enable search functionality
              placeholder="Select supplier"
              classNames={getSelectClassNames({
                isDisabled: false,
                selectProps: {},
                isSelected: false,
                value: watch("supplier_id"), // Use watch to get the current value
              })} // Pass parameters here
              formatOptionLabel={(data) => (
                <li
                  className={`mb-1 block cursor-pointer select-none truncate rounded px-2 py-2 transition duration-200 ${
                    !data.isSelected
                      ? `bg-gray-200 text-gray-900`
                      : `bg-blue-800 text-gray-200`
                  }`}
                >
                  {data.label}
                </li>
              )}
            />
          </div>
          <div>
            {searchParams && (
              <button
                type="button"
                className="w-full rounded bg-red-500 px-4 py-2 text-white hover:bg-red-600"
                onClick={() => {
                  setSearchParams(null);
                  sessionStorage.removeItem("searchInvoiceParams");
                  reset({
                    // Explicitly reset form fields
                    email: "",
                    phone_number: "",
                    booking_date: "",
                    departure_date: "",
                    invoice_number: "",
                    supplier_id: "",
                  }); // ✅ Fully reset the form
                }}
              >
                Cancel
              </button>
            )}
          </div>
          <div className="">
            <button
              type="submit"
              className="w-full items-end rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
            >
              Search
            </button>
          </div>
        </div>

        {errors.form && (
          <p className="mt-2 text-sm text-red-500">{errors.form.message}</p>
        )}
      </form>

      {searchParams && (
        <InvoiceList
          key={JSON.stringify(searchParams)}
          searchComponentParams={searchParams}
        />
      )}
    </div>
  );
}
